import { Box, Stack, Typography } from '@mui/material';
import { DataGridProProps } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { ReactElement } from 'react';
import { ItemCard } from '../components/ItemCard';
import { SeverityBadge } from '../components/SeverityBadge';
import { PATHS } from '../../../routing/routes';
import { CustomNoRowsOverlay } from '../../components/CustomNoRowsOverlay';
import { FilterConfiguration } from '../../components/FilterPanel/types';
import { FilterStatusBadge } from '../../components/FilterStatusBadge';
import { PaginatedTableController, PaginatedTableState, PrefilledDataConfig } from '../../controllers/PaginatedTableController';
import { getFilterStatusLabel } from '../../enums/FilterStatus';
import { SEVERITY_COLORS, getLabel } from '../../enums/Severity';
import { DataGridStyleOverrides } from '../../style/data-grid-styles';
import { DefaultHeaderConfiguration, FieldConfiguration } from '../../types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../types/pagination';
import { UrlHelper } from '../../utils/UrlHelper';
import { bind } from '../../utils/decorators';
import { InstancesTableSkeleton } from '../components/skeletons/InstancesTableSkeleton';
import { Instance } from '../models/Instance';

export type Props = {
  reportId: number;
  initialInstances: PaginationResult<Instance> | null;
  parentLoading: boolean;
};

type State = PaginatedTableState & {};

export const getInstanceClassname = (instance: Instance) => {
  return `instance-${instance.getNumber()}-${instance.getSeverity()}`;
};

export class InstancesTableController<P extends Props = Props> extends PaginatedTableController<Instance, P, State> {
  static defaultProps = {
    initialInstances: null,
  };

  protected getPrefilledConfig() {
    return {
      prefillActive: true,
      initialLoading: this.props.parentLoading,
      initialData: this.props.initialInstances,
    } as PrefilledDataConfig<Instance>;
  }

  protected isLoading(): boolean {
    return super.isLoading() || this.props.parentLoading;
  }

  protected getAdditionalDataGridProps(): Partial<DataGridProProps<Instance>> {
    return {
      ...super.getAdditionalDataGridProps(),
      classes: {
        cell: DataGridStyleOverrides.CELL_NO_BORDER,
        row: DataGridStyleOverrides.ROW_AUTO_HEIGHT,
        columnHeaders: DataGridStyleOverrides.CUSTOM_HEADER,
        // Preserve the native virtual scroller heights when there are no elements to correctly
        // display the "no rows" overlay
        virtualScrollerContent: this.state.totalCount === 0 ? '' : DataGridStyleOverrides.DYNAMIC_ROW_HEIGHT,
        virtualScrollerRenderZone: DataGridStyleOverrides.DYNAMIC_ROW_HEIGHT,
      },
      slots: {
        noRowsOverlay: () => (
          <Box sx={{ height: 1, borderTop: '1px solid', borderColor: 'grey.100' }}>
            <CustomNoRowsOverlay label={t('instancesListEmpty')} />
          </Box>
        ),
      },
    };
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [];
  }

  protected getDefaultHeaderConfigurations(): DefaultHeaderConfiguration {
    return { title: t('instances') };
  }

  protected renderToolbar(): ReactElement {
    // Override the default toolbar, it will be displayed as the header of the only
    // column of the table
    return <></>;
  }

  protected getFieldConfigurations(): FieldConfiguration<Instance>[] {
    return [
      {
        fieldKey: 'instance',
        label: '',
        columnProps: {
          cellClassName: 'px-0',
        },
        renderHeader: this.renderHeader,
        render: (model, { tabIndex }) => this.renderInstanceRow(model, tabIndex),
      },
    ];
  }

  protected renderInstanceRow(model: Instance, tabIndex: 0 | -1) {
    return (
      <ItemCard
        tabIndex={tabIndex}
        className={getInstanceClassname(model)}
        borderLeftColor={SEVERITY_COLORS[model.getSeverity()].main}
        grow={true}
        ariaLabel={t('a11y.report.issues.instanceListItem', {
          instanceNumber: model.getNumber(),
          instanceData: model.getData(),
          severity: getLabel(model.getSeverity()),
          filterStatus: getFilterStatusLabel(model.getFilterStatus(), { omitFullyKept: true }),
        })}
        href={UrlHelper.generatePath(
          PATHS.REPORT_ISSUE_INSTANCE,
          {
            reportId: this.props.reportId.toString(),
            issueId: model.getIssueNumber().toString(),
            instanceId: model.getNumber().toString(),
          },
          { keepQueryParams: true }
        )}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <SeverityBadge severity={model.getSeverity()}>
            {model.getIssueNumber()}.{model.getNumberLocal()}
          </SeverityBadge>
          <Box>
            <FilterStatusBadge filterStatus={model.getFilterStatus()} noBorder />
            <Typography className="instance-data" sx={{ pt: 0.25, wordBreak: 'break-word' }}>
              {model.getData()}
            </Typography>
          </Box>
        </Stack>
      </ItemCard>
    );
  }

  protected async fetchData(_: CursorInfo): Promise<PaginationResult<Instance>> {
    return BLANK_PAGINATION_RESULT;
  }

  protected renderTable(tableComponent: ReactElement) {
    if (this.state.initialDataLoad) {
      // Use a custom skeleton instead of the built-in automatic skeleton.
      // Each row is a `ItemCard` with its own internal layout
      return <InstancesTableSkeleton rows={this.getDefaultPageSize()} />;
    }
    return <Box className="instances-list">{tableComponent}</Box>;
  }

  @bind
  protected renderHeader() {
    return (
      <Box sx={{ width: 1 }} data-role="paginated-table-toolbar">
        <Box sx={{ height: '4rem', px: 2, display: 'flex', alignItems: 'center' }}>{this.renderLeftToolbarElements()}</Box>
      </Box>
    );
  }
}
